// client/src/pages/LgmProductNavigator.tsx
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';
import ProductsReview from './ProductsReview';
import ManufacturerReview from './ManufacturerReview';
import Grades from './Grades';
import TherapeuticAreas from './TherapeuticAreas';
import useAuth from 'auth/auth';
import DevTools from 'components/DevTools';
import { DebugLayout } from 'components/DebugLayout';
import PrimaryNav from 'components/PrimaryNav';
import CasDecisionPanel from 'components/CasDecisionPanel';
import CasMergeSplitPanel from 'components/CasMergeSplitPanel';
import type { CasQuarantineQueueContext } from './CasQuarantineQueuePage';
// import QuotesReview from './QuotesReview';
import { useDeleteAllProducts } from 'services/api/domains/products/hooks';
import AuditLogs from './AuditLogs';
import { useViewportSize } from 'shared/hooks/useViewportSize';
import { useViewBackStack } from 'navigation/useViewBackStack';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import type { AdvancedSearchQuery } from 'components/TableSearch/types';
import type { UnifiedSearchState } from 'components/UnifiedSearchBar';
import {
  encodeFilter,
  decodeFilter,
  hasActiveRules,
} from 'navigation/searchFilterCodec';
import {
  encodeFdaSourceUnlinkedParams,
  decodeFdaSourceUnlinkedParams,
} from 'navigation/fdaSourceUnlinkedParams';
import type { FdaSourceLinkParams } from 'navigation/fdaSourceUnlinkedParams';
import { useABTest } from '../contexts/ABTestContext';
import { LGM_ORANGE } from 'theme/colors';
import { GRAY_200, GRAY_500 } from 'theme/grayScale';

// Lazy load heavy components to reduce initial bundle size
const DashboardMetrics = lazy(() => import('./DashboardMetrics'));
const ManufacturerMap = lazy(() => import('./ManufacturerMap'));
const ValidationJobsPage = lazy(() => import('./ValidationJobsPage'));
const ProductUploadBatchesPage = lazy(
  () => import('./ProductUploadBatchesPage')
);
const ProductUploadBatchPage = lazy(() => import('./ProductUploadBatchPage'));
const CasQuarantineQueuePage = lazy(() => import('./CasQuarantineQueuePage'));
const CasQuarantineReviewPage = lazy(() => import('./CasQuarantineReviewPage'));
const TrainingVideoPlayerPage = lazy(() => import('./TrainingVideoPlayerPage'));

// Loading fallback component for lazy-loaded routes
const LoadingFallback: React.FC<{ message: string }> = ({ message }) => (
  <div style={loadingStyles.containerStyle}>
    <div style={loadingStyles.spinnerStyle} />
    <p style={loadingStyles.textStyle}>{message}</p>
  </div>
);

// ─── Last-view persistence ────────────────────────────────────────────────────
export type View =
  | 'products'
  | 'manufacturer_master'
  | 'manufacturer_map'
  | 'grades'
  | 'therapeutic_areas'
  | 'quotes'
  | 'audit_logs'
  | 'dashboard_metrics'
  | 'validation_jobs'
  | 'product_upload_batches'
  | 'cas_quarantine_queue'
  | 'training_video_player';

const LAST_VIEW_KEY = 'lgm-last-view';
const LAST_BATCH_ID_KEY = 'lgm-last-product-upload-batch-id';
const LAST_CAS_QUARANTINE_PRODUCT_ID_KEY = 'lgm-last-cas-quarantine-product-id';

const VALID_VIEWS = new Set<string>([
  'products',
  'manufacturer_master',
  'manufacturer_map',
  'grades',
  'therapeutic_areas',
  'quotes',
  'audit_logs',
  'dashboard_metrics',
  'validation_jobs',
  'product_upload_batches',
  'cas_quarantine_queue',
  'training_video_player',
]);

// Views that require admin role. cas_quarantine_queue is deliberately absent
// -- its read endpoints stay products:read (RBAC-1 only narrowed the WRITE
// routes), so a viewer/editor can browse the queue same as an admin; only
// the decision-panel actions inside it are quarantine-queue:write-gated.
const ADMIN_ONLY_VIEWS = new Set<string>([
  'grades',
  'therapeutic_areas',
  'audit_logs',
  'validation_jobs',
  'product_upload_batches',
]);

function getSavedView(): View {
  try {
    const raw = localStorage.getItem(LAST_VIEW_KEY);
    if (raw !== null && VALID_VIEWS.has(raw)) return raw as View;
  } catch {
    // localStorage unavailable; fall through
  }
  return 'products';
}

// Only meaningful when the saved view is 'product_upload_batches' -- a
// hard refresh while viewing a specific batch's detail page otherwise
// dropped the user back to the batch list, since `view` was persisted but
// the open batch id was not (issue #349 manual QA finding).
function getSavedBatchId(view: View): number | null {
  if (view !== 'product_upload_batches') return null;
  try {
    const raw = localStorage.getItem(LAST_BATCH_ID_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

// Same reasoning as getSavedBatchId above, for the CAS quarantine review
// screen (issue #662, CQR-017.3: "reachable directly by URL... not just by
// clicking a list row" -- this app has no real URL routing, so a persisted
// id across a refresh is what makes that requirement true here, same
// mechanism the pre-existing upload-batch feature already uses). Only the
// id is restored, not the filtered queue's productIds/queueContext -- those
// are transient page state the list itself doesn't persist either, so a
// restored session starts as a single-item queue (prev/next effectively
// disabled) rather than reconstructing filters that were never saved.
function getSavedCasQuarantineProductId(view: View): number | null {
  if (view !== 'cas_quarantine_queue') return null;
  try {
    const raw = localStorage.getItem(LAST_CAS_QUARANTINE_PRODUCT_ID_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

const VIEW_TITLES: Record<View, string> = {
  products: 'Products',
  manufacturer_master: 'Manufacturers',
  manufacturer_map: 'Facilities Map',
  grades: 'Product Types',
  therapeutic_areas: 'Therapeutic Areas',
  quotes: 'RFQs',
  audit_logs: 'Audit Logs',
  dashboard_metrics: 'Metrics',
  validation_jobs: 'Validation Jobs',
  product_upload_batches: 'Product Uploads',
  cas_quarantine_queue: 'CAS Quarantine Queue',
  training_video_player: 'Training Video',
};

function getViewTitle(view: View): string {
  return `${VIEW_TITLES[view]} - LGM Navigator`;
}

// Real URL paths per view (metrics-deep-linking, MDL-007) — written into the
// address bar via useViewBackStack's `getPath`, so a plain view switch (and,
// for 'products', a deep-link filter) is shareable/bookmarkable. The server
// already serves index.html for any path (api/index.js's catch-all route),
// so no server-side change is required for these to resolve on a fresh load.
const VIEW_PATHS: Record<View, string> = {
  products: '/products',
  manufacturer_master: '/manufacturers',
  manufacturer_map: '/manufacturers/map',
  grades: '/product-types',
  therapeutic_areas: '/therapeutic-areas',
  quotes: '/rfqs',
  audit_logs: '/audit-logs',
  dashboard_metrics: '/metrics',
  validation_jobs: '/validation-jobs',
  product_upload_batches: '/product-uploads',
  cas_quarantine_queue: '/cas-quarantine',
  training_video_player: '/training',
};

const PATH_TO_VIEW: Record<string, View> = Object.fromEntries(
  Object.entries(VIEW_PATHS).map(([view, path]) => [path, view as View])
);

function getPathForView(view: View): string {
  return VIEW_PATHS[view];
}

// Resolves the current `window.location.pathname` to a known View, for
// boot-time hydration from a pasted/bookmarked deep-link URL. Returns null
// for an unrecognized path (e.g. the bare '/' root, or a stale/foreign path)
// so the caller can fall back to the existing localStorage-based
// getSavedView() convention instead.
function getViewFromPathname(pathname: string): View | null {
  return PATH_TO_VIEW[pathname] ?? null;
}

// Boot-time initial view resolution (MDL-007): a real URL path takes
// priority over the localStorage-persisted last view, so a pasted/bookmarked
// deep link always lands where it says, even if the user's last session left
// off somewhere else on this browser.
function getInitialView(): View {
  const fromPath = getViewFromPathname(window.location.pathname);
  return fromPath ?? getSavedView();
}

// Boot-time filter hydration (MDL-007): only meaningful when the resolved
// initial view matches the target view -- a pasted/bookmarked deep-link URL
// for one view shouldn't leak its `filter` param into a different view. A
// missing or malformed `filter` query param decodes to null (decodeFilter's
// own contract), which this function passes straight through -- the target
// view then simply loads unfiltered rather than crashing on a stale/tampered
// URL.
function getInitialFilterQueryForView(
  initialView: View,
  targetView: View
): AdvancedSearchQuery | null {
  if (initialView !== targetView) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('filter');
  if (!encoded) {
    return null;
  }
  return decodeFilter(encoded);
}

// Boot-time basic-search-term hydration, sibling to
// getInitialFilterQueryForView above -- a plain `?search=<term>` deep link
// (bug fix, 2026-09-29: the URL previously only ever reflected an ADVANCED
// filter set by a Metrics-card click, never a basic-mode search term, and
// never anything set by direct on-page interaction). Same
// initialView-must-match-targetView guard and fail-closed-to-null contract.
function getInitialSearchTermForView(
  initialView: View,
  targetView: View
): string | null {
  if (initialView !== targetView) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const term = params.get('search');
  return term ? term : null;
}

// Boot-time FDA-source-unlinked/linked hydration (Task FSU-003), sibling to
// getInitialFilterQueryForView above -- a real path +
// ?fdaSource=&linkFilter=... deep link from a Navigator-tab "Not Yet
// Sourceable"/"Sourceable via Navigator" metric card. Same
// initialView-must-match-targetView guard and same fail-closed-to-null
// contract as the AdvancedSearchQuery version, kept as a fully separate
// query-param pair (not the `filter` codec) per
// docs/features/fda-source-record-browser.md Architecture Decision 4.
function getInitialFdaSourceUnlinkedForView(
  initialView: View,
  targetView: View
): PendingFdaSourceUnlinked | null {
  if (initialView !== targetView) {
    return null;
  }
  return decodeFdaSourceUnlinkedParams(
    new URLSearchParams(window.location.search)
  );
}

// Keep-alive tab rendering (Requirement 3 / Architecture Decision 7): once a
// tab has been visited, it stays mounted and is hidden via `display: none`
// instead of being unmounted, so its filter/sort/pagination/scroll state
// survives a switch away and back. This is scoped to this top-level tab
// conditional only -- modal-level conditional rendering within each page is
// untouched.
function hiddenUnless(isActive: boolean): React.CSSProperties {
  return isActive ? {} : { display: 'none' };
}

// Same keep-alive semantics as `hiddenUnless`, but the active branch also
// threads a definite height down through the flex chain instead of a bare
// `{}` (block, auto height). Pages whose own layout already assumes a
// height-bounded ancestor (ProductsReview/ManufacturerReview's
// pageContainerStyle: height '100%') need this so their internal grid
// scrolls in place instead of the whole tab -- search bar included --
// scrolling as one block inside mainContentStyle (issue #631 follow-up).
function hiddenUnlessFill(isActive: boolean): React.CSSProperties {
  return isActive
    ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }
    : { display: 'none' };
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Cross-view filter payload (manufacturer-product-linking, Task 2.1) ──────
// LgmProductNavigator's view state has always been a bare string enum with no
// payload -- this is the first mechanism for one view to carry filter context
// into another. `manufacturerName` is carried alongside the id purely for
// display -- the target grid's advanced-search summary shows it instead of
// the raw id (matching how a manually-picked async-select filter renders),
// per UX feedback on PR #563.
export interface PendingManufacturerFilter {
  manufacturerId: number;
  manufacturerName?: string;
}

// Generalized Products-view deep-link payload (metrics-deep-linking,
// MDL-006) -- carries an arbitrary AdvancedSearchQuery instead of a
// hardcoded manufacturer_id, so any caller (Manufacturer's "View Products"
// button, a Navigator-tab metric card) can deep-link into Products filtered
// on any field the advanced-search UI already supports. The reverse
// direction (Products -> Manufacturer, PendingManufacturerFilter above)
// stayed manufacturer_id-only until the Metrics "Manufacturers" card needed
// an arbitrary-field deep link (product_count > 0) -- PendingManufacturersFilter
// below covers that case as a second, independent slot rather than
// overloading the id-based one.
export interface PendingProductsFilter {
  query: AdvancedSearchQuery;
}

// Metrics -> Manufacturers deep-link payload, mirroring PendingProductsFilter
// above (arbitrary AdvancedSearchQuery, not a single id).
export interface PendingManufacturersFilter {
  query: AdvancedSearchQuery;
}

// Metrics -> Products, FDA Sources view "linked"/"unlinked only" deep-link
// payload (Task FSU-003; `linked` + filters added for the "Sourceable via
// Navigator" deep link, human direction, 2026-09-28). Structurally
// unrelated to PendingProductsFilter above -- it targets the FDA Sources
// search view (external OB/NDC/USDMF records), not an AdvancedSearchQuery
// filter over dbo.Products -- see docs/features/fda-source-record-
// browser.md Architecture Decision 4.
export type PendingFdaSourceUnlinked = FdaSourceLinkParams;

/** JSON.stringify(null) === 'null' would otherwise collide with the "no
 * state" sentinel used throughout lastAppliedFdaSourceUnlinkedRef's
 * comparisons -- this keeps `null` meaning `null`, not the string "null". */
function fdaSourceUnlinkedSignature(
  params: PendingFdaSourceUnlinked | null
): string | null {
  return params ? JSON.stringify(params) : null;
}
// ─────────────────────────────────────────────────────────────────────────────

const LgmProductNavigator: React.FC = () => {
  const { view, setView, replaceView } = useViewBackStack<View>(
    getInitialView,
    getViewTitle,
    getPathForView
  );

  // Synchronous mirror of `view`, read by the URL-sync callbacks below
  // instead of `view` itself -- those callbacks need a STABLE identity
  // (empty `useCallback` deps) so a child's memoized `onSearchChange`
  // handler never closes over a stale one, but they also need to know the
  // CURRENT view at call time. Updated during render (not an effect), so
  // it's always current by the time any child's mount effect can fire.
  const viewRef = useRef(view);
  viewRef.current = view;

  // Tracks every view visited this session so it can stay mounted (hidden,
  // not removed) once switched away from -- see `hiddenUnless` above.
  const visitedViewsRef = useRef<Set<View>>(new Set());
  visitedViewsRef.current.add(view);
  const isVisited = useCallback(
    (v: View) => visitedViewsRef.current.has(v),
    []
  );

  // Cross-view filter payload (Task 2.1) -- set by navigateToXFiltered,
  // consumed (and cleared) by the target view's grid on mount. Two
  // independent slots since a filter could in principle be pending in both
  // directions at once (e.g. rapid back-and-forth navigation).
  // Lazy-initialized from the boot URL (MDL-007) so a pasted/bookmarked
  // deep-link filter is applied on ProductsReview's very first mount, with
  // no flash of unfiltered data — mirrors the existing
  // selectedProductUploadBatchId/getSavedBatchId lazy-init convention below.
  const [pendingProductsFilter, setPendingProductsFilter] =
    useState<PendingProductsFilter | null>(() => {
      const query = getInitialFilterQueryForView(view, 'products');
      return query ? { query } : null;
    });
  const [pendingManufacturerFilter, setPendingManufacturerFilter] =
    useState<PendingManufacturerFilter | null>(null);
  const [pendingManufacturersFilter, setPendingManufacturersFilter] =
    useState<PendingManufacturersFilter | null>(() => {
      const query = getInitialFilterQueryForView(view, 'manufacturer_master');
      return query ? { query } : null;
    });
  // Metrics -> Products FDA Sources "unlinked only" deep link (Task
  // FSU-003) -- same lazy-init-from-boot-URL convention as the two slots
  // above, reusing productsFilterNonce (below) to force ProductsReview's
  // remount, since both slots target the same 'products' view/component.
  const [pendingFdaSourceUnlinked, setPendingFdaSourceUnlinked] =
    useState<PendingFdaSourceUnlinked | null>(() =>
      getInitialFdaSourceUnlinkedForView(view, 'products')
    );

  // Boot-time basic-search-term hydration (bug fix, 2026-09-29) -- mirrors
  // pendingProductsFilter/pendingManufacturersFilter above, but for a plain
  // `?search=<term>` deep link rather than an AdvancedSearchQuery. Only
  // ever produced by a boot-time URL or a Back/Forward pop (see
  // handlePopStateFilterSync below) -- no other view deep-links a basic
  // term the way navigateToProductsFiltered/navigateToManufacturersFiltered
  // do for AdvancedSearchQuery, so there's no separate "navigateToX"
  // constructor for this slot.
  const [pendingProductsSearchTerm, setPendingProductsSearchTerm] = useState<
    string | null
  >(() => getInitialSearchTermForView(view, 'products'));
  const [pendingManufacturersSearchTerm, setPendingManufacturersSearchTerm] =
    useState<string | null>(() =>
      getInitialSearchTermForView(view, 'manufacturer_master')
    );

  // Forces a full remount of ProductsReview/ManufacturerReview (via `key`,
  // below) on every "View Products"/"View Manufacturer" click. Keep-alive
  // tabs (above) mean these pages normally stay mounted across a plain tab
  // switch, so their mount-only filter-seeding lazy initializers never
  // re-run for one -- a real deep-link click needs the fresh mount those
  // initializers assume in order to actually apply the new manufacturer
  // filter, rather than leaving whatever filter the page already had.
  const [productsFilterNonce, setProductsFilterNonce] = useState(0);
  const [manufacturerFilterNonce, setManufacturerFilterNonce] = useState(0);

  // Tracks the open batch so the render block below can switch between the
  // list page and ProductUploadBatchPage (Seq 11's detail page). Restored
  // from localStorage on mount (mirrors `view`'s own getSavedView()) so a
  // hard refresh while viewing a specific batch's detail page stays there
  // instead of falling back to the batch list.
  const [selectedProductUploadBatchId, setSelectedProductUploadBatchId] =
    useState<number | null>(() => getSavedBatchId(getSavedView()));
  const openProductUploadBatch = useCallback(
    (batchId: number) => {
      setSelectedProductUploadBatchId(batchId);
      setView('product_upload_batches');
    },
    [setView]
  );

  // Tracks the open product (the ordered product_ids of the page it was
  // opened from, for previous/next, plus the exact queueContext that
  // produced them so the review page can fetch further server pages) so
  // the render block below can switch between the queue list and
  // CasQuarantineReviewPage (issue #662, CQR-016/017) -- same list/detail
  // swap shape as selectedProductUploadBatchId above. The product id alone
  // is persisted across a refresh (see getSavedCasQuarantineProductId);
  // productIds/queueContext are transient page state, restored as a
  // single-item degenerate context on reload.
  const [selectedCasQuarantineProduct, setSelectedCasQuarantineProduct] =
    useState<{
      productId: number;
      productIds: number[];
      queueContext?: CasQuarantineQueueContext;
    } | null>(() => {
      const restoredId = getSavedCasQuarantineProductId(getSavedView());
      return restoredId
        ? { productId: restoredId, productIds: [restoredId] }
        : null;
    });
  const openCasQuarantineProduct = useCallback(
    (
      productId: number,
      productIds: number[],
      queueContext: CasQuarantineQueueContext
    ) => {
      setSelectedCasQuarantineProduct({ productId, productIds, queueContext });
    },
    []
  );
  const navigateCasQuarantineProduct = useCallback((productId: number) => {
    setSelectedCasQuarantineProduct(prev =>
      prev ? { ...prev, productId } : { productId, productIds: [productId] }
    );
  }, []);
  const closeCasQuarantineProduct = useCallback(() => {
    setSelectedCasQuarantineProduct(null);
  }, []);

  // Opening a product for review is a sub-navigation within the
  // cas_quarantine_queue view (useViewBackStack only tracks the top-level
  // view), so it needs its own back-dismiss entry -- same mechanism every
  // real modal in this app uses, and the review page's own auto-advance
  // (from one product to the next) is a non-back-driven replace, not a new
  // dismissible layer, matching canDismiss's default.
  useModalBackDismiss({
    isOpen: selectedCasQuarantineProduct !== null,
    onClose: closeCasQuarantineProduct,
  });

  // Tracks the encoded `?filter=` value the mounted Products/Manufacturers
  // grid is CURRENTLY showing, independent of `pendingProductsFilter`/
  // `pendingManufacturersFilter` -- those are one-shot hydration signals
  // that get cleared by `onInitialFilterConsumed` once the grid applies
  // them (see `clearPendingProductsFilter`/`clearPendingManufacturerFilter`
  // below), so comparing against them post-consumption always sees `null`
  // regardless of what's actually displayed (Copilot review, PR #829 round
  // 3 -- a filtered-to-unfiltered Back landed on this exact false match).
  // `null` means "no filter" (matches `URLSearchParams.get`'s own null-for-
  // absent contract), initialized from the boot URL only when it actually
  // resolves to that view.
  const lastAppliedProductsFilterRef = useRef<string | null>(
    getViewFromPathname(window.location.pathname) === 'products'
      ? new URLSearchParams(window.location.search).get('filter')
      : null
  );
  const lastAppliedManufacturersFilterRef = useRef<string | null>(
    getViewFromPathname(window.location.pathname) === 'manufacturer_master'
      ? new URLSearchParams(window.location.search).get('filter')
      : null
  );
  // Same tracked-baseline convention as the two refs above, for the plain
  // `?search=<term>` basic-search deep link (bug fix, 2026-09-29).
  const lastAppliedProductsSearchRef = useRef<string | null>(
    getViewFromPathname(window.location.pathname) === 'products'
      ? new URLSearchParams(window.location.search).get('search')
      : null
  );
  const lastAppliedManufacturersSearchRef = useRef<string | null>(
    getViewFromPathname(window.location.pathname) === 'manufacturer_master'
      ? new URLSearchParams(window.location.search).get('search')
      : null
  );
  // Same Back/Forward-resync convention as the two refs above, for the
  // `?fdaSource=...&linkFilter=...` deep link (Copilot review, PR #832) --
  // tracks a JSON-stringified signature of the whole decoded payload (not
  // just the source), since linkFilter/filters can now also change between
  // two FDA-source deep links, not just the source key itself.
  const lastAppliedFdaSourceUnlinkedRef = useRef<string | null>(
    getViewFromPathname(window.location.pathname) === 'products'
      ? fdaSourceUnlinkedSignature(
          getInitialFdaSourceUnlinkedForView('products', 'products')
        )
      : null
  );

  const navigateToProductsFiltered = useCallback(
    (query: AdvancedSearchQuery) => {
      setPendingProductsFilter({ query });
      // This click lands on a plain `?filter=` deep link, so any
      // FDA-source-unlinked state a prior click/pop applied is no longer
      // current. Without clearing the state itself, the nonce-triggered
      // remount below would re-run ProductsRowGroupingDataGrid's lazy
      // `initialFdaSourceUnlinked` initializer with the STALE object,
      // re-entering FDA Sources mode instead of the new plain filter; and
      // without clearing the ref, a later pop to a plain `/products` URL
      // would compare against a stale non-null baseline and wrongly decide
      // "no change," leaving the grid showing an FDA banner it shouldn't
      // (Copilot review, PR #832 round 7).
      setPendingFdaSourceUnlinked(null);
      setProductsFilterNonce(n => n + 1);
      setView('products');
      const encoded = encodeFilter(query);
      lastAppliedProductsFilterRef.current = encoded;
      lastAppliedFdaSourceUnlinkedRef.current = null;
      // Attach the encoded filter onto the just-pushed '/products' history
      // entry (MDL-007) -- replaceState edits that same entry in place
      // rather than adding a new one, so the resulting URL
      // ('/products?filter=...') is a complete, shareable deep link and
      // Back still returns to wherever the user was before this click.
      window.history.replaceState(
        window.history.state,
        '',
        `${VIEW_PATHS.products}?filter=${encoded}`
      );
    },
    [setView]
  );

  const navigateToManufacturerFiltered = useCallback(
    (manufacturerId: number, manufacturerName?: string) => {
      setPendingManufacturerFilter({ manufacturerId, manufacturerName });
      setManufacturerFilterNonce(n => n + 1);
      setView('manufacturer_master');
      // setView pushes a bare '/manufacturers' path with no filter query
      // (see useViewBackStack's getPath) -- this id-based navigation never
      // attaches one, so the applied-filter baseline for this view is now
      // genuinely "none", not whatever it was before this click.
      lastAppliedManufacturersFilterRef.current = null;
    },
    [setView]
  );

  const navigateToManufacturersFiltered = useCallback(
    (query: AdvancedSearchQuery) => {
      setPendingManufacturersFilter({ query });
      setManufacturerFilterNonce(n => n + 1);
      setView('manufacturer_master');
      const encoded = encodeFilter(query);
      lastAppliedManufacturersFilterRef.current = encoded;
      // Same real-URL treatment as navigateToProductsFiltered above --
      // '/manufacturers?filter=...' is a complete, shareable deep link.
      window.history.replaceState(
        window.history.state,
        '',
        `${VIEW_PATHS.manufacturer_master}?filter=${encoded}`
      );
    },
    [setView]
  );

  // Browser Back/Forward restores `view` via useViewBackStack's own popstate
  // handler, but not `pendingProductsFilter`/`pendingManufacturersFilter` --
  // those are only ever set by a click (navigateToProductsFiltered/
  // navigateToManufacturersFiltered above) or the mount-time lazy
  // initializer, neither of which re-runs on a pop. Visiting two different
  // filtered deep links to the same view, then Back, left the (still-
  // mounted, keep-alive) grid showing the later filter while the restored
  // URL/address-bar showed the earlier one (Copilot review, PR #829 round
  // 2). This effect re-derives the filter from the just-restored URL on
  // every pop and only updates + remounts (via the nonce) when it actually
  // differs from the `lastApplied*FilterRef` baseline above, so a plain
  // tab-switch Back/Forward with no filter change still keeps the grid's
  // in-place state.
  useEffect(() => {
    function handlePopStateFilterSync(): void {
      const poppedView = getViewFromPathname(window.location.pathname);
      const params = new URLSearchParams(window.location.search);
      const rawFilterParam = params.get('filter');
      const rawSearchParam = params.get('search');
      if (poppedView === 'products') {
        if (rawFilterParam !== lastAppliedProductsFilterRef.current) {
          const query = getInitialFilterQueryForView(poppedView, 'products');
          setPendingProductsFilter(query ? { query } : null);
          setProductsFilterNonce(n => n + 1);
          lastAppliedProductsFilterRef.current = rawFilterParam;
        }
        // Same pop-resync as the `?filter=` case above, but for a plain
        // `?search=<term>` basic-search deep link (bug fix, 2026-09-29).
        if (rawSearchParam !== lastAppliedProductsSearchRef.current) {
          setPendingProductsSearchTerm(
            getInitialSearchTermForView(poppedView, 'products')
          );
          setProductsFilterNonce(n => n + 1);
          lastAppliedProductsSearchRef.current = rawSearchParam;
        }
        // Same pop-resync as the `?filter=` case above, for the separate
        // `?fdaSource=...&linkFilter=...` param pair (Copilot review, PR
        // #832) -- otherwise Back/Forward between two FDA-source deep
        // links (or one and a plain products view) leaves the mounted
        // grid on the stale source/linkFilter/filters while the URL shows
        // the restored one.
        const fdaSourceUnlinked = getInitialFdaSourceUnlinkedForView(
          poppedView,
          'products'
        );
        const fdaSourceUnlinkedSig =
          fdaSourceUnlinkedSignature(fdaSourceUnlinked);
        if (fdaSourceUnlinkedSig !== lastAppliedFdaSourceUnlinkedRef.current) {
          setPendingFdaSourceUnlinked(fdaSourceUnlinked);
          setProductsFilterNonce(n => n + 1);
          lastAppliedFdaSourceUnlinkedRef.current = fdaSourceUnlinkedSig;
        }
      } else if (poppedView === 'manufacturer_master') {
        if (rawFilterParam !== lastAppliedManufacturersFilterRef.current) {
          const query = getInitialFilterQueryForView(
            poppedView,
            'manufacturer_master'
          );
          setPendingManufacturersFilter(query ? { query } : null);
          setManufacturerFilterNonce(n => n + 1);
          lastAppliedManufacturersFilterRef.current = rawFilterParam;
        }
        // Same pop-resync as the `?filter=` case above, for a plain
        // `?search=<term>` basic-search deep link (bug fix, 2026-09-29).
        if (rawSearchParam !== lastAppliedManufacturersSearchRef.current) {
          setPendingManufacturersSearchTerm(
            getInitialSearchTermForView(poppedView, 'manufacturer_master')
          );
          setManufacturerFilterNonce(n => n + 1);
          lastAppliedManufacturersSearchRef.current = rawSearchParam;
        }
      }
    }
    window.addEventListener('popstate', handlePopStateFilterSync);
    return () =>
      window.removeEventListener('popstate', handlePopStateFilterSync);
  }, []);

  const clearPendingProductsFilter = useCallback(() => {
    setPendingProductsFilter(null);
  }, []);

  const clearPendingProductsSearchTerm = useCallback(() => {
    setPendingProductsSearchTerm(null);
  }, []);

  const clearPendingManufacturersSearchTerm = useCallback(() => {
    setPendingManufacturersSearchTerm(null);
  }, []);

  // Keeps the address bar in sync with the CURRENT Products search/filter
  // state, not just the moment a Metrics-card deep link is clicked (bug
  // fix, 2026-09-29) -- every UnifiedSearchBar change (basic term or
  // advanced query), reported by ProductsRowGroupingDataGrid's
  // onSearchChange, flows through here. Deliberately has an empty
  // dependency array (reads `viewRef.current`, not `view` directly) so its
  // identity never changes -- ProductsRowGroupingDataGrid's own
  // handleSearchChange is itself a stable `useCallback`, and a
  // `view`-dependent identity here would go stale in that child's closure
  // after the first render (see `viewRef`'s own doc comment above).
  const syncProductsSearchUrl = useCallback((state: UnifiedSearchState) => {
    if (viewRef.current !== 'products') return;
    if (state.mode === 'advanced' && hasActiveRules(state.advancedQuery)) {
      const encoded = encodeFilter(state.advancedQuery);
      if (
        encoded === lastAppliedProductsFilterRef.current &&
        lastAppliedProductsSearchRef.current === null
      ) {
        return;
      }
      lastAppliedProductsFilterRef.current = encoded;
      lastAppliedProductsSearchRef.current = null;
      window.history.replaceState(
        window.history.state,
        '',
        `${VIEW_PATHS.products}?filter=${encoded}`
      );
      return;
    }
    if (state.mode === 'basic' && state.basicTerm) {
      if (
        state.basicTerm === lastAppliedProductsSearchRef.current &&
        lastAppliedProductsFilterRef.current === null
      ) {
        return;
      }
      lastAppliedProductsSearchRef.current = state.basicTerm;
      lastAppliedProductsFilterRef.current = null;
      window.history.replaceState(
        window.history.state,
        '',
        `${VIEW_PATHS.products}?search=${encodeURIComponent(state.basicTerm)}`
      );
      return;
    }
    if (
      lastAppliedProductsFilterRef.current === null &&
      lastAppliedProductsSearchRef.current === null
    ) {
      return;
    }
    lastAppliedProductsFilterRef.current = null;
    lastAppliedProductsSearchRef.current = null;
    window.history.replaceState(window.history.state, '', VIEW_PATHS.products);
  }, []);

  // Mirrors syncProductsSearchUrl above for the Manufacturers view -- same
  // stable-identity/viewRef reasoning applies to ManufacturerReview's own
  // handleSearchChange.
  const syncManufacturersSearchUrl = useCallback(
    (state: UnifiedSearchState) => {
      if (viewRef.current !== 'manufacturer_master') return;
      if (state.mode === 'advanced' && hasActiveRules(state.advancedQuery)) {
        const encoded = encodeFilter(state.advancedQuery);
        if (
          encoded === lastAppliedManufacturersFilterRef.current &&
          lastAppliedManufacturersSearchRef.current === null
        ) {
          return;
        }
        lastAppliedManufacturersFilterRef.current = encoded;
        lastAppliedManufacturersSearchRef.current = null;
        window.history.replaceState(
          window.history.state,
          '',
          `${VIEW_PATHS.manufacturer_master}?filter=${encoded}`
        );
        return;
      }
      if (state.mode === 'basic' && state.basicTerm) {
        if (
          state.basicTerm === lastAppliedManufacturersSearchRef.current &&
          lastAppliedManufacturersFilterRef.current === null
        ) {
          return;
        }
        lastAppliedManufacturersSearchRef.current = state.basicTerm;
        lastAppliedManufacturersFilterRef.current = null;
        window.history.replaceState(
          window.history.state,
          '',
          `${VIEW_PATHS.manufacturer_master}?search=${encodeURIComponent(
            state.basicTerm
          )}`
        );
        return;
      }
      if (
        lastAppliedManufacturersFilterRef.current === null &&
        lastAppliedManufacturersSearchRef.current === null
      ) {
        return;
      }
      lastAppliedManufacturersFilterRef.current = null;
      lastAppliedManufacturersSearchRef.current = null;
      window.history.replaceState(
        window.history.state,
        '',
        VIEW_PATHS.manufacturer_master
      );
    },
    []
  );

  // Keep-alive tabs (ProductsReview/ManufacturerReview stay mounted, hidden,
  // across a plain tab switch -- see `hiddenUnlessFill` above) mean a
  // retained search/filter keeps being displayed after switching away and
  // back, but `setView`'s own pushState only ever writes the BARE path for
  // the target view (`useViewBackStack`'s `getPath`) -- it has no way to
  // know a hidden, still-mounted page has non-default state. Left alone,
  // returning to a tab left the address bar reading `/products` (or
  // `/manufacturers`) with no query even though the grid still showed the
  // retained filter/search (Copilot review, PR #840 round 2). Re-derive the
  // URL every time `view` changes, from the same last-applied baselines the
  // sync callbacks above already maintain -- a real deep-link click or a
  // popstate resync has always already updated those baselines to match the
  // URL by the time this runs (both happen synchronously, before this
  // effect's commit-phase turn), so this is a no-op for every case except
  // the plain-tab-switch gap it exists to close.
  useEffect(() => {
    const current = window.location.pathname + window.location.search;
    if (view === 'products') {
      let target: string;
      if (lastAppliedFdaSourceUnlinkedRef.current) {
        const params = JSON.parse(
          lastAppliedFdaSourceUnlinkedRef.current
        ) as PendingFdaSourceUnlinked;
        target = `${VIEW_PATHS.products}?${encodeFdaSourceUnlinkedParams(
          params.source,
          params.linkFilter,
          {
            obType: params.obType,
            ndcFinished: params.ndcFinished,
            dmfStatus: params.dmfStatus,
            cohort: params.cohort,
          }
        )}`;
      } else if (lastAppliedProductsFilterRef.current) {
        target = `${VIEW_PATHS.products}?filter=${lastAppliedProductsFilterRef.current}`;
      } else if (lastAppliedProductsSearchRef.current) {
        target = `${VIEW_PATHS.products}?search=${encodeURIComponent(
          lastAppliedProductsSearchRef.current
        )}`;
      } else {
        target = VIEW_PATHS.products;
      }
      if (current !== target) {
        window.history.replaceState(window.history.state, '', target);
      }
    } else if (view === 'manufacturer_master') {
      let target: string;
      if (lastAppliedManufacturersFilterRef.current) {
        target = `${VIEW_PATHS.manufacturer_master}?filter=${lastAppliedManufacturersFilterRef.current}`;
      } else if (lastAppliedManufacturersSearchRef.current) {
        target = `${VIEW_PATHS.manufacturer_master}?search=${encodeURIComponent(
          lastAppliedManufacturersSearchRef.current
        )}`;
      } else {
        target = VIEW_PATHS.manufacturer_master;
      }
      if (current !== target) {
        window.history.replaceState(window.history.state, '', target);
      }
    }
  }, [view]);

  const navigateToFdaSourceUnlinked = useCallback(
    (params: PendingFdaSourceUnlinked) => {
      setPendingFdaSourceUnlinked(params);
      // Mirrors navigateToProductsFiltered's symmetric clear above -- a
      // stale pendingProductsFilter would otherwise survive the
      // nonce-triggered remount and re-populate the grid's internal-mode
      // advanced search state even while displaying the FDA Sources banner
      // (Copilot review, PR #832 round 7).
      setPendingProductsFilter(null);
      setProductsFilterNonce(n => n + 1);
      setView('products');
      // Mirrors navigateToProductsFiltered's ref bookkeeping in both
      // directions: record the FDA-source baseline this click just applied
      // (the popstate handler previously only updated this ref itself, so
      // a click never did -- leaving it stale for the next pop to compare
      // against), and clear the plain-filter baseline since this deep link
      // carries no `?filter=` (Copilot review, PR #832 round 7). Also
      // clears the plain-search baseline (Copilot review, PR #840 round 2)
      // -- a prior basic-search term on Products otherwise survives here,
      // and the remounted grid's mount-time "sync initial state to parent"
      // emission (an empty basic state, since this deep link seeds no
      // search term) reaches syncProductsSearchUrl's "clear the URL" branch
      // ONLY when both refs read null; leaving the stale search ref set
      // made that branch fire and overwrite this fdaSource URL with a bare
      // '/products' immediately after it was written.
      lastAppliedFdaSourceUnlinkedRef.current =
        fdaSourceUnlinkedSignature(params);
      lastAppliedProductsFilterRef.current = null;
      lastAppliedProductsSearchRef.current = null;
      // Same replaceState-onto-the-just-pushed-entry pattern as
      // navigateToProductsFiltered above (MDL-007) -- the resulting URL
      // ('/products?fdaSource=...&linkFilter=...') is a complete, shareable
      // deep link.
      window.history.replaceState(
        window.history.state,
        '',
        `${VIEW_PATHS.products}?${encodeFdaSourceUnlinkedParams(
          params.source,
          params.linkFilter,
          {
            obType: params.obType,
            ndcFinished: params.ndcFinished,
            dmfStatus: params.dmfStatus,
            cohort: params.cohort,
          }
        )}`
      );
    },
    [setView]
  );

  const clearPendingFdaSourceUnlinked = useCallback(() => {
    setPendingFdaSourceUnlinked(null);
  }, []);

  // Undoes navigateToFdaSourceUnlinked's replaceState when the user
  // dismisses the "Not Yet Sourceable"/"Sourceable via Navigator" banner
  // (its X button) -- without this, the URL stays
  // `?fdaSource=...&linkFilter=...` after dismissal, so a reload (or a
  // shared/bookmarked link) would re-decode those params on mount and
  // silently re-open the banner the user just closed.
  //
  // Also resets lastAppliedFdaSourceUnlinkedRef to null alongside the URL
  // (Copilot review, PR #832 round 3) -- otherwise the ref stays set to
  // the dismissed source, and a later Back to plain `/products` reads as
  // a filter CHANGE (ref no longer matches the popped state's null), which
  // remounts the grid via the nonce bump and drops the Internal-mode state
  // dismissal was meant to preserve.
  const clearFdaSourceUnlinkedUrl = useCallback(() => {
    window.history.replaceState(window.history.state, '', VIEW_PATHS.products);
    lastAppliedFdaSourceUnlinkedRef.current = null;
  }, []);

  // Deep-link-to-product-modal support for ProductsReview (Task 2.2's
  // remount-on-click convention). The upload batch detail page's "View
  // Product" action used to drive this (issue #349, Sub-task 11.3) but now
  // renders the product modal locally instead, without leaving the batch
  // page (Test 6.11 back-navigation fix) -- `pendingProductIdToOpen` stays
  // permanently null today, kept only because `ProductsReview` still
  // declares the prop for any future deep-link caller.
  const [pendingProductIdToOpen, setPendingProductIdToOpen] = useState<
    number | null
  >(null);
  const clearPendingProductIdToOpen = useCallback(() => {
    setPendingProductIdToOpen(null);
  }, []);

  // Clears both manufacturer deep-link slots -- id-based (Products -> one
  // Manufacturer) and query-based (Metrics -> Manufacturers filtered).
  // ManufacturerReview fires a single onInitialFilterConsumed regardless of
  // which slot supplied its initial filter; clearing both is a harmless
  // no-op on whichever slot wasn't set.
  const clearPendingManufacturerFilter = useCallback(() => {
    setPendingManufacturerFilter(null);
    setPendingManufacturersFilter(null);
  }, []);

  const { instance, account, isAuthenticated, isAdmin, roles } = useAuth();
  const { getTestValue } = useABTest();
  const hideProductType = getTestValue('hide_product_type');

  const [showDevTools, setShowDevTools] = useState(false);
  // Kept for ProductsReview's own grid-refresh effect keyed on
  // deleteAllStatus.status -- the DevTools "Delete All Products" button
  // that used to trigger this mutation was removed, but ProductsReview
  // still declares the prop for any future caller.
  const deleteAllProductsMutation = useDeleteAllProducts();

  // Restore last-viewed page after authentication, with role gating. Falls
  // back to 'products' if the target view requires admin and the user isn't
  // one.
  //
  // A real URL path (getViewFromPathname) is authoritative over the
  // localStorage-saved view, same priority order as getInitialView()'s own
  // boot-time resolution -- a pasted/bookmarked deep link must not be
  // silently replaced by whatever view a PREVIOUS session left in
  // localStorage once account resolves (Copilot review, 2026-09-24: relying
  // on the "persist current view" effect below to have already overwritten
  // localStorage by this point is fragile effect-ordering, not a real
  // guarantee -- re-deriving the target from the URL here removes that
  // reliance entirely). The same admin/hidden-flag gating still applies to
  // whichever target wins, so a deep link to an admin-only view by a
  // non-admin still redirects.
  useEffect(() => {
    if (account) {
      const target =
        getViewFromPathname(window.location.pathname) ?? getSavedView();
      const targetRequiresAdmin = ADMIN_ONLY_VIEWS.has(target) && !isAdmin;
      // hide_product_type AB flag: don't restore a 'grades' target once the
      // nav tab that leads there has been hidden.
      const targetIsHiddenProductType = target === 'grades' && hideProductType;
      replaceView(
        targetRequiresAdmin || targetIsHiddenProductType ? 'products' : target
      );
    }
  }, [instance, account, isAdmin, hideProductType, replaceView]);

  // The restore effect above only guards the mount-time saved-view read.
  // Browser Back/Forward restores a history entry directly via popstate
  // (useViewBackStack's handlePop calls setViewState with no gating at
  // all), bypassing that effect entirely -- a stale admin-only history
  // entry (e.g. from before a permission change, or before this flag
  // turned on) would otherwise land on a view whose render is suppressed
  // below, showing a blank page with no way back except the browser's own
  // Back button. Re-validate on every `view` change, not just on mount.
  // Two independent gates, checked separately rather than merged into one
  // condition -- RBAC (isAdmin) is permanent, the AB flag is temporary;
  // conflating them would make the flag's eventual removal harder to spot.
  //
  // Gated on `account`, same as the mount-restore effect above: `isAdmin`
  // (client/src/auth/auth.ts) starts `false` and only resolves to its real
  // value in a later effect/render. Without this gate, a genuine admin's
  // saved admin-only view would get wiped on the very first render (before
  // `isAdmin` settles) and then persisted over the real saved view by the
  // localStorage-sync effect below -- a false-positive redirect, not the
  // popstate-bypass case this effect exists to catch (code-review finding).
  useEffect(() => {
    if (account && ADMIN_ONLY_VIEWS.has(view) && !isAdmin) {
      replaceView('products');
    }
  }, [account, view, isAdmin, replaceView]);

  useEffect(() => {
    if (view === 'grades' && hideProductType) {
      replaceView('products');
    }
  }, [view, hideProductType, replaceView]);

  // Persist the active view to localStorage so it survives page refreshes
  // and Azure AD authentication redirects.
  useEffect(() => {
    try {
      localStorage.setItem(LAST_VIEW_KEY, view);
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [view]);

  // Mirrors the effect above for the open batch id -- removes the key
  // entirely when back at the list (null) so a later refresh from the
  // list page doesn't resurrect a stale batch id.
  useEffect(() => {
    try {
      if (selectedProductUploadBatchId === null) {
        localStorage.removeItem(LAST_BATCH_ID_KEY);
      } else {
        localStorage.setItem(
          LAST_BATCH_ID_KEY,
          String(selectedProductUploadBatchId)
        );
      }
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [selectedProductUploadBatchId]);

  // Mirrors the effect above for the open CAS quarantine product id.
  useEffect(() => {
    try {
      if (selectedCasQuarantineProduct === null) {
        localStorage.removeItem(LAST_CAS_QUARANTINE_PRODUCT_ID_KEY);
      } else {
        localStorage.setItem(
          LAST_CAS_QUARANTINE_PRODUCT_ID_KEY,
          String(selectedCasQuarantineProduct.productId)
        );
      }
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [selectedCasQuarantineProduct]);

  useEffect(() => {
    console.log('🔎 Current view:', view, 'Roles:', roles);
  }, [view, roles]);

  const { heightClass } = useViewportSize();
  const isSmall = heightClass === 'sm';
  const headerPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' }
    : {};
  const logoSize: React.CSSProperties = isSmall ? { height: '52px' } : {};
  const mainPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.75rem', paddingBottom: '0.75rem' }
    : {};

  return (
    <div style={styles.mainContainerStyle}>
      {/* Top Navigation Bar */}
      <div
        style={{ ...styles.headerContainerStyle, ...headerPadding }}
        data-layout-role="app-header">
        <div style={styles.headerContentStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Pharma"
            style={{ ...styles.logoStyle, ...logoSize }}
          />

          <PrimaryNav
            view={view}
            setView={setView}
            isAdmin={isAdmin}
            showDevTools={showDevTools}
            onToggleDevTools={() => setShowDevTools(prev => !prev)}
            onLogout={() => instance.logoutRedirect()}
          />
        </div>
      </div>

      {/* Developer Tools Panel */}
      {showDevTools && <DevTools />}

      {/* Main Content */}
      <div style={{ ...styles.mainContentStyle, ...mainPadding }}>
        {!isAuthenticated ? (
          <p style={styles.waitingTextStyle}>🔐 Waiting for login...</p>
        ) : (
          <>
            {/* Products */}
            {isVisited('products') && (
              <div style={hiddenUnlessFill(view === 'products')}>
                <ProductsReview
                  key={productsFilterNonce}
                  deleteAllStatus={deleteAllProductsMutation}
                  initialProductsFilter={pendingProductsFilter?.query ?? null}
                  onInitialFilterConsumed={clearPendingProductsFilter}
                  initialProductsSearchTerm={pendingProductsSearchTerm}
                  onInitialSearchTermConsumed={clearPendingProductsSearchTerm}
                  onSearchStateChange={syncProductsSearchUrl}
                  forceSearchStateOverride={productsFilterNonce > 0}
                  initialFdaSourceUnlinked={pendingFdaSourceUnlinked}
                  onFdaSourceUnlinkedConsumed={clearPendingFdaSourceUnlinked}
                  onFdaSourceUnlinkedBannerClear={clearFdaSourceUnlinkedUrl}
                  onViewManufacturer={navigateToManufacturerFiltered}
                  onUpload={
                    isAdmin
                      ? () => setView('product_upload_batches')
                      : undefined
                  }
                  initialProductIdToOpen={pendingProductIdToOpen}
                  onInitialProductIdConsumed={clearPendingProductIdToOpen}
                />
              </div>
            )}
            {/* Manufacturers */}
            {isVisited('manufacturer_master') && (
              <div style={hiddenUnlessFill(view === 'manufacturer_master')}>
                <ManufacturerReview
                  key={manufacturerFilterNonce}
                  initialManufacturerId={
                    pendingManufacturerFilter?.manufacturerId
                  }
                  initialManufacturerName={
                    pendingManufacturerFilter?.manufacturerName
                  }
                  initialManufacturersFilter={
                    pendingManufacturersFilter?.query ?? null
                  }
                  onInitialFilterConsumed={clearPendingManufacturerFilter}
                  initialManufacturersSearchTerm={
                    pendingManufacturersSearchTerm
                  }
                  onInitialSearchTermConsumed={
                    clearPendingManufacturersSearchTerm
                  }
                  onSearchStateChange={syncManufacturersSearchUrl}
                  forceSearchStateOverride={manufacturerFilterNonce > 0}
                  onViewProducts={navigateToProductsFiltered}
                />
              </div>
            )}
            {/* Grades */}
            {isVisited('grades') && isAdmin && !hideProductType && (
              <div style={hiddenUnlessFill(view === 'grades')}>
                <Grades />
              </div>
            )}
            {/* Therapeutic Areas */}
            {isVisited('therapeutic_areas') && isAdmin && (
              <div style={hiddenUnlessFill(view === 'therapeutic_areas')}>
                <TherapeuticAreas />
              </div>
            )}
            {/* Facilities Map */}
            {isVisited('manufacturer_map') && (
              <div style={hiddenUnless(view === 'manufacturer_map')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading facilities..." />
                  }>
                  <ManufacturerMap />
                </Suspense>
              </div>
            )}
            {/* RFQs */}
            {/* {view === 'quotes' && <QuotesReview />} */}
            {/* Dashboard Metrics */}
            {isVisited('dashboard_metrics') && (
              <div style={hiddenUnless(view === 'dashboard_metrics')}>
                <Suspense
                  fallback={<LoadingFallback message="Loading metrics..." />}>
                  <DashboardMetrics
                    onViewProducts={navigateToProductsFiltered}
                    onViewManufacturers={navigateToManufacturersFiltered}
                    onViewFdaSourceUnlinked={navigateToFdaSourceUnlinked}
                  />
                </Suspense>
              </div>
            )}
            {/* Audit Logs */}
            {isVisited('audit_logs') && (
              <div style={hiddenUnlessFill(view === 'audit_logs')}>
                <AuditLogs />
              </div>
            )}
            {/* Validation Jobs (issue #637) -- admin-only, same as the
                other ADMIN_ONLY_VIEWS. */}
            {isVisited('validation_jobs') && isAdmin && (
              <div style={hiddenUnless(view === 'validation_jobs')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading validation jobs..." />
                  }>
                  <ValidationJobsPage isActive={view === 'validation_jobs'} />
                </Suspense>
              </div>
            )}
            {/* Product Upload Batches (issue #349, Seq/UPS-010 + UPS-011). */}
            {isVisited('product_upload_batches') && isAdmin && (
              <div style={hiddenUnlessFill(view === 'product_upload_batches')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading upload batches..." />
                  }>
                  {selectedProductUploadBatchId !== null ? (
                    <ProductUploadBatchPage
                      batchId={selectedProductUploadBatchId}
                      onBack={() => setSelectedProductUploadBatchId(null)}
                    />
                  ) : (
                    <ProductUploadBatchesPage
                      isActive={view === 'product_upload_batches'}
                      onOpenBatch={openProductUploadBatch}
                      onWatchWalkthrough={() =>
                        setView('training_video_player')
                      }
                    />
                  )}
                </Suspense>
              </div>
            )}
            {/* Training Video Player (training-video pipeline port, see
                .github/ai/sessions/2026-09-training-videos.md). Reachable
                today only via the "Watch a walkthrough" button on
                ProductUploadBatchesPage (the list page, not the per-batch
                detail page) -- no resource library yet (Decision 4), so
                this is the sole entry point for V1. */}
            {isVisited('training_video_player') && (
              <div style={hiddenUnlessFill(view === 'training_video_player')}>
                <Suspense
                  fallback={<LoadingFallback message="Loading video..." />}>
                  <TrainingVideoPlayerPage
                    appSlug="product-upload"
                    file="overview.mp4"
                    title="Product Sheet Upload — Walkthrough"
                    // training_video_player is deliberately not in
                    // ADMIN_ONLY_VIEWS (Decision 1 -- any role can watch),
                    // but its only real entry point (the button on
                    // ProductUploadBatchesPage) IS admin-only. A non-admin
                    // can still land here via the persisted LAST_VIEW_KEY
                    // restore-on-mount path (e.g. they were admin last
                    // session, or lost the role since). Unconditionally
                    // sending Back to product_upload_batches would put a
                    // non-admin on a view that isAdmin-gates its own render
                    // (line ~537) to nothing -- a blank content area.
                    onBack={() =>
                      setView(isAdmin ? 'product_upload_batches' : 'products')
                    }
                  />
                </Suspense>
              </div>
            )}
            {/* CAS Quarantine Queue (issue #662, CQR-016/017). Reachable by
                every authenticated role -- see ADMIN_ONLY_VIEWS's comment
                above for why this one is deliberately not admin-gated. */}
            {isVisited('cas_quarantine_queue') && (
              <div style={hiddenUnlessFill(view === 'cas_quarantine_queue')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading quarantine queue..." />
                  }>
                  {/* Kept mounted (visibility-toggled, not unmounted) even
                      while a product's review is open, so its own filter/
                      page state survives Back instead of remounting to
                      defaults (Copilot review, PR #792 round 4). */}
                  <div
                    style={hiddenUnlessFill(
                      selectedCasQuarantineProduct === null
                    )}>
                    <CasQuarantineQueuePage
                      isActive={
                        view === 'cas_quarantine_queue' &&
                        selectedCasQuarantineProduct === null
                      }
                      onOpenProduct={openCasQuarantineProduct}
                    />
                  </div>
                  {selectedCasQuarantineProduct !== null && (
                    <CasQuarantineReviewPage
                      productId={selectedCasQuarantineProduct.productId}
                      productIds={selectedCasQuarantineProduct.productIds}
                      queueContext={selectedCasQuarantineProduct.queueContext}
                      onNavigate={navigateCasQuarantineProduct}
                      onBack={closeCasQuarantineProduct}
                      canWrite={isAdmin}
                      renderDecisionPanel={(row, detail, onResolved) =>
                        row.available_actions.can_split ? (
                          <CasMergeSplitPanel
                            productId={detail.product.product_id}
                            row={row}
                            onResolved={onResolved}
                          />
                        ) : (
                          <CasDecisionPanel
                            productId={detail.product.product_id}
                            establishedCas={
                              detail.product.is_cas_verified
                                ? detail.product.cas_number
                                : null
                            }
                            casSources={detail.cas_sources}
                            row={row}
                            onResolved={onResolved}
                          />
                        )
                      }
                    />
                  )}
                </Suspense>
              </div>
            )}
          </>
        )}
      </div>
      <DebugLayout />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'inherit', // font-lgm
  } as React.CSSProperties,
  headerContainerStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    borderBottom: `1px solid ${GRAY_200}`, // border-b
    paddingBottom: '1rem', // pb-4
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1rem', // py-4
  } as React.CSSProperties,
  headerContentStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
  } as React.CSSProperties,
  logoStyle: {
    height: '75px',
    objectFit: 'contain',
  } as React.CSSProperties,
  mainContentStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'auto',
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1.5rem', // py-6
    paddingBottom: '1.5rem',
  } as React.CSSProperties,
  waitingTextStyle: {
    color: GRAY_500, // text-gray-500
  } as React.CSSProperties,
};

const loadingStyles: Record<string, React.CSSProperties> = {
  containerStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    fontFamily: 'var(--font-lgm, sans-serif)',
  },
  spinnerStyle: {
    width: '48px',
    height: '48px',
    border: `4px solid ${GRAY_200}`,
    borderTop: `4px solid ${LGM_ORANGE}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  textStyle: {
    marginTop: '16px',
    color: GRAY_500,
    fontSize: '14px',
  },
};

export default LgmProductNavigator;
